// Site-wide Areas view: every area across all floors in one table, with
// its floor. Complements the per-floor drill-down (FloorDetailPage →
// AreaListPanel). Read-only; areas are created per-floor or via layout import.

import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Pill } from "../../components/ui/Pill";
import { Table } from "../../components/ui/Table";
import { PageHeader } from "../../components/shell/PageHeader";

import { useSiteAreas, type SiteArea } from "./useSiteAreas";

interface Props {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
}

export function SiteAreasPanel({ accountSlug, tenantSlug, siteSlug }: Props) {
  const areas = useSiteAreas(accountSlug, tenantSlug, siteSlug);

  return (
    <>
      <PageHeader
        title="Areas"
        subtitle="Every area across the site, with its floor. Create areas under a floor (Overview → floor) or via layout import."
      />

      {areas.error != null && (
        <ErrorBanner error={areas.error} title="Failed to load areas" />
      )}
      {areas.loading && <p style={{ color: "var(--text-dim)" }}>Loading…</p>}

      {!areas.loading && !areas.error && (
        <Table<SiteArea>
          columns={[
            { key: "label", header: "Area", render: (a) => a.label },
            {
              key: "slug",
              header: "Slug",
              render: (a) => (
                <code style={{ fontSize: "0.78rem", color: "var(--text-dim)" }}>
                  {a.slug}
                </code>
              ),
            },
            {
              key: "space_type",
              header: "Space type",
              render: (a) => <Pill tone="info">{a.space_type}</Pill>,
            },
            {
              key: "floor",
              header: "Floor",
              render: (a) => (
                <span>
                  {a.floorLabel}{" "}
                  <code style={{ fontSize: "0.74rem", color: "var(--text-dim)" }}>
                    {a.floorSlug}
                  </code>
                </span>
              ),
            },
          ]}
          rows={areas.items}
          rowKey={(a) => a.id}
          empty={
            <EmptyState
              title="No areas on this site"
              description="Add floors and areas under Overview, or import a layout CSV from the site header."
            />
          }
        />
      )}
    </>
  );
}
